import { useState, createContext, useMemo, ReactNode, useEffect } from 'react'
import { FieldError } from 'react-hook-form'

import { Elements, RecurlyProvider } from 'lib/recurly'
import { getSubscriptionOptions, RECURLY_PUBLIC_KEY } from 'utils/payment'
import { useUser } from 'hooks/use-user'
import { shouldUpdateBillingInfo } from 'utils/userAuth'
import { translate } from 'utils/translation'
import { SubscriptionPlan } from 'schemas'
import { isLifetimeValid } from 'utils/functions'

import { CheckoutFormValues } from '../components/form/schemas'

declare global {
  interface Window {
    recurly?: {
      configure?: (config: unknown) => void
    }
  }
}

export interface ServerErrorParams {
  type: string
  message: string
  code?: string
}

interface RecurlyContextType {
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  recurlyErrors: RecurlyErrors
  setRecurlyErrors: (errors: RecurlyErrors) => void
  showThreeDAuth: boolean
  setShowThreeDAuth: (show: boolean) => void
  serverError: ServerErrorParams | null
  setServerError: (error: ServerErrorParams | null) => void
  currentSubscription: SubscriptionPlan | undefined
  interval: string
  tier: string
  promoFeature?: boolean
  onContinue?: (callback?: () => void) => void
  onBlockPayment?: (data: unknown) => void
  hasAIWAddon?: boolean
  addonDetail?: {
    code: string
  }
  submitType?: string
  onUpdateSuccess?: () => void
}

export const RecurlyContext = createContext<RecurlyContextType | null>(null)

export type RecurlyErrors = Partial<
  Record<keyof CheckoutFormValues, FieldError>
>

export interface RecurlyContainerProps {
  interval: string
  tier: string
  promoFeature?: boolean
  onContinue?: (callback?: () => void) => void
  onBlockPayment?: (data: unknown) => void
  children: ReactNode
  hasAIWAddon?: boolean
  addonDetail?: {
    code: string
  }
  submitType?: string
  onUpdateSuccess?: () => void
}

export const RecurlyContainer = ({
  children,
  ...props
}: RecurlyContainerProps) => {
  const { interval, tier, promoFeature, hasAIWAddon } = props

  const user = useUser()
  const [recurlyErrors, setRecurlyErrors] = useState<RecurlyErrors>({})
  const [showThreeDAuth, setShowThreeDAuth] = useState(false)
  const [serverError, setServerError] = useState(
    shouldUpdateBillingInfo()
      ? {
          type: 'warning',
          message: translate('old_lifetime_update_billing_info_modal'),
        }
      : null,
  )

  const [isRecurlyReady, setIsRecurlyReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // @ts-ignore TODO: BT-325 removed arguments
  const isLifetimeLeft = isLifetimeValid(user.lifetime_offer_until)
  const subscriptionOptions = getSubscriptionOptions({
    countryCode: user.countryCode,
    tier,
    _isLifetimeValid: isLifetimeLeft,
    promoFeature,
  }) as SubscriptionPlan[]

  const currentSubscription = subscriptionOptions.find(
    (subscription) => subscription?.planLabel === interval,
  )

  const contextValue = useMemo(
    () => ({
      ...props,
      currentSubscription,
      recurlyErrors,
      setRecurlyErrors,
      showThreeDAuth,
      setShowThreeDAuth,
      serverError,
      setServerError,
      isLoading,
      setIsLoading,
      hasAIWAddon,
    }),
    [
      props,
      hasAIWAddon,
      currentSubscription,
      recurlyErrors,
      showThreeDAuth,
      serverError,
      isLoading,
    ],
  )

  useEffect(() => {
    const checkRecurlyReady = () => {
      const recurlyWindow = window
      if (recurlyWindow.recurly) setIsRecurlyReady(true)
    }

    checkRecurlyReady()
    window.addEventListener('recurly:load', checkRecurlyReady)

    return () => {
      window.removeEventListener('recurly:load', checkRecurlyReady)
    }
  }, [])

  return (
    <RecurlyContext.Provider value={contextValue}>
      <RecurlyProvider publicKey={RECURLY_PUBLIC_KEY}>
        <Elements>{isRecurlyReady && children}</Elements>
      </RecurlyProvider>
    </RecurlyContext.Provider>
  )
}
